// NGRP 100-Day Maharashtra Launch Plan — tactical, week-by-week playbook
// authored by the party leadership. Rendered inside /roadmap after the 10-phase blueprint.

export const HUNDRED_DAY_PLAN = {
  title: "100-Day Maharashtra Launch Plan",
  subtitle: "Week-by-week: whom to approach, what to convene, and how to identify the first 100 founding leaders.",
  intro:
    "The 100-day plan converts the Launch Blueprint into an operational schedule. It is measured in weeks, not press conferences \u2014 with clear ownership at every step and a public listening loop from day one.",

  north_star: {
    label: "North star",
    body:
      "By Day 100, publish the Maharashtra People\u2019s Governance Report 2026 \u2014 evidence of listening, not just speeches. Founders, organisers and pilot districts operational; the party has stopped talking about itself and started answering to citizens.",
  },

  weeks: [
    {
      range: "Week 1",
      days: "Days 1\u20137",
      title: "Founders identified. Charter locked. Silent build.",
      approach: [
        "5\u20137 credible founding partners (co-founders) across UPSC/MPSC alumni, engineers, doctors, lawyers, entrepreneurs, retired administrators.",
        "1 trusted legal + compliance advisor.",
        "1 finance / audit advisor.",
      ],
      convene: [
        "3 closed-door founder meetings (2 hours each).",
        "Sign an internal NGRP Founder Charter \u2014 ethics, transparency, no-personality-cult clauses.",
      ],
      deliverables: [
        "Founder Charter v1.0 signed.",
        "9 functional team leads shortlisted (Organisation / Policy / Legal / Comms / Tech / Membership / Training / Finance / Grassroots).",
        "Party name, tagline, emblem and interim constitution locked.",
      ],
    },
    {
      range: "Week 2",
      days: "Days 8\u201314",
      title: "Interim Executive & Legal readiness.",
      approach: [
        "Confirm interim State Executive (9 leads).",
        "Engage chartered accountant for Section 29A / ECI-readiness accounting and party bank account setup.",
      ],
      convene: [
        "1 interim State Executive meeting.",
        "1 legal review session on ECI Section 29A registration path, party constitution and symbol notice.",
      ],
      deliverables: [
        "Bank account + accounting stack live.",
        "Legal roadmap document (ECI + tax + FCRA implications) v1.0.",
        "Interim NGRP internal governance rules published to the founders.",
      ],
    },
    {
      range: "Weeks 3\u20134",
      days: "Days 15\u201328",
      title: "First 100 Founding Leaders \u2014 shortlist and interview.",
      approach: [
        "Every founder brings 15\u201320 credible names from their network (target ~120 shortlist).",
        "Coverage target: at least 20 of Maharashtra\u2019s 36 districts represented.",
        "Explicit mix: UPSC/MPSC aspirants, engineers, doctors, lawyers/CAs, teachers, entrepreneurs, farmers, women leaders, retired officers, youth, grassroots social workers.",
      ],
      convene: [
        "Structured 45-minute virtual interviews \u2014 5 questions: integrity, public work, willingness, credibility, constitutional commitment.",
        "1 Founders\u2019 Council review meeting to finalise 100.",
      ],
      deliverables: [
        "Founding 100 confirmed \u2014 published as roles (not just names).",
        "District coverage map \u2014 shows where NGRP already has a founding presence and where it doesn\u2019t.",
        "Every founder assigned a specific problem in Maharashtra they will help solve.",
      ],
    },
    {
      range: "Weeks 5\u20136",
      days: "Days 29\u201342",
      title: "The Founding Convention (invitation-only) & public soft-launch.",
      approach: [
        "Founding 100 + advisors + selected media.",
        "Public soft-launch: Namaskar letter, Emblem, Founding Vision, Constitution and Launch Blueprint go live on ngrp.in.",
      ],
      convene: [
        "1 invitation-only Founding Convention (1 day, hybrid) \u2014 ratify constitution, elect interim office-bearers, adopt Ethics & Integrity Commission charter.",
        "1 public digital launch \u2014 Namaskar video address, 20-minute press briefing (no rally).",
      ],
      deliverables: [
        "NGRP Constitution v1.0 ratified.",
        "Interim Executive elected (fixed term with 90-day review).",
        "Website live with Reform Dialogue open to citizens.",
        "Ethics & Integrity Commission charter adopted.",
      ],
    },
    {
      range: "Weeks 7\u20138",
      days: "Days 43\u201356",
      title: "100 \u00d7 10 \u2014 build the first 1,000 organisers.",
      approach: [
        "Each of the 100 founders identifies ~10 high-quality potential members in their district/city.",
        "Focus on Active Members and Leadership Cadre only \u2014 supporters can join separately, later.",
      ],
      convene: [
        "10 regional Governance Dialogues (Konkan, Western MH, Vidarbha, Marathwada, North MH, Mumbai MMR, Pune, Nashik, Nagpur, Aurangabad) \u2014 100\u2013200 invited participants each.",
        "Every dialogue answers one operational question: \u201cIf Maharashtra could reform 5 systems in 5 years, what should they be?\u201d",
      ],
      deliverables: [
        "1,000 verified Active Members with published district affiliation.",
        "10 dialogue transcripts + digital summaries \u2014 published on the site.",
        "Manifesto Draft v0.1 seeded from citizen input.",
      ],
    },
    {
      range: "Weeks 9\u201310",
      days: "Days 57\u201370",
      title: "Pilot districts declared. Village/Ward teams begin.",
      approach: [
        "Select 5\u20137 pilot districts based on founder density, geographic mix and governance-issue severity (e.g. Nashik, Pune, Aurangabad/Chh. Sambhajinagar, Nagpur, Kolhapur, Amravati, Thane).",
        "Recruit Village/Ward Coordinators (~11 members per unit) in pilot talukas.",
      ],
      convene: [
        "1 District Council convening in each pilot district (functional teams: Organisation, Policy, Legal, Comms, Tech, Membership, Training, Public Grievances, Local Development).",
        "1 statewide operational review with Founders + District Councils.",
      ],
      deliverables: [
        "Pilot district launch plans (public).",
        "First 50\u2013100 Village/Ward Committees seeded across pilot talukas.",
        "NGRP Public Issues Register live \u2014 first 500 citizen issues logged.",
      ],
    },
    {
      range: "Weeks 11\u201312",
      days: "Days 71\u201384",
      title: "NGRP School of Public Leadership \u2014 Cohort 1.",
      approach: [
        "Open applications to UPSC/MPSC aspirants, engineers, doctors, lawyers, teachers, entrepreneurs, farmers, researchers.",
        "Target 200 Cohort-1 participants with reserved seats for women and youth.",
      ],
      convene: [
        "1 Cohort-1 orientation (hybrid).",
        "Weekly modules: Constitution, Panchayati Raj, Public Finance, Ethics, Public Communication.",
        "Assignment: adopt 1 village/ward \u2192 identify 25 issues \u2192 propose solutions \u2192 engage administration.",
      ],
      deliverables: [
        "Cohort-1 selected and enrolled.",
        "Public curriculum + reading list published.",
        "Field assignments issued and tracked.",
      ],
    },
    {
      range: "Weeks 13\u201314",
      days: "Days 85\u201398",
      title: "10,000 Governance Reformers scale-up & data consolidation.",
      approach: [
        "Structured public campaign \u2014 open online registration with a light Supporter / Active Member / Leadership Cadre tiering.",
        "Ground amplification via Founding 100 and Cohort-1 field assignments.",
      ],
      convene: [
        "35\u201340 taluka-level Governance Dialogues \u2014 hosted by district councils, not the state HQ.",
        "1 State Council review of dashboards, issues register and finances.",
      ],
      deliverables: [
        "10,000+ verified Governance Reformers.",
        "Issues Register: 5,000+ citizen issues, classified by department.",
        "Taluka Governance Dashboards live for pilot districts.",
      ],
    },
    {
      range: "Days 99\u2013100",
      days: "Days 99\u2013100",
      title: "Publish Maharashtra People\u2019s Governance Report 2026.",
      approach: [
        "State-level review + report launch \u2014 hybrid, low-cost, evidence-first.",
        "Distribute to citizens, media, academia and government (in that order).",
      ],
      convene: [
        "1 State Convention (hybrid) \u2014 100-day review, adoption of Manifesto Draft v0.2, roadmap for Days 101\u2013365.",
        "1 public press conference \u2014 20 minutes, statistics-led, no political rally.",
      ],
      deliverables: [
        "Maharashtra People\u2019s Governance Report 2026 (public PDF + web).",
        "Manifesto Draft v0.2 (informed by citizen input).",
        "Public Days 101\u2013365 roadmap and district expansion plan.",
      ],
    },
  ],

  targets: [
    { label: "Founding leaders", value: "100", note: "Day 28" },
    { label: "Active members", value: "1,000", note: "Day 56" },
    { label: "Village/Ward units", value: "50\u2013100", note: "Day 70" },
    { label: "Cohort-1 leaders in training", value: "200", note: "Day 84" },
    { label: "Governance reformers", value: "10,000", note: "Day 98" },
    { label: "Citizen issues logged", value: "5,000+", note: "Day 98" },
    { label: "Districts with functional council", value: "5\u20137 pilots", note: "Day 70" },
    { label: "Public governance report", value: "1", note: "Day 100" },
  ],

  guardrails: [
    "No cash memberships. Every rupee is banked, receipted, audited.",
    "No political posts distributed casually. Every role has a 90-day performance review.",
    "No large political rallies during the 100 days \u2014 governance dialogues only.",
    "No candidate promises. Candidate selection is a separate, later, transparent process.",
    "No personality cult. Every communication references the institution first, individuals second.",
  ],

  next_365:
    "After Day 100: expand pilot districts to statewide coverage; open Cohort-2 of the Leadership Academy; begin Assembly Constituency Governance Teams; complete ECI-registration workstream; publish quarterly transparency reports.",
};
